import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { estimateItemsService } from '../services/estimateItems.service';
import type { EstimateItemInput } from '../types/estimate.types';

export function useEstimateItems(estimateId?: string) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['estimates', 'items', estimateId],
    queryFn: () => estimateItemsService.getByEstimateId(estimateId!),
    enabled: !!estimateId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['estimates', 'items', estimateId] });
    queryClient.invalidateQueries({ queryKey: ['estimates', 'detail', estimateId] });
    queryClient.invalidateQueries({ queryKey: ['estimates'] });
  };

  const createMutation = useMutation({
    mutationFn: (input: EstimateItemInput) => estimateItemsService.create(input),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<EstimateItemInput> }) =>
      estimateItemsService.update(id, updates),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => estimateItemsService.delete(id),
    onSuccess: invalidate,
  });

  return {
    items: query.data?.items || [],
    stats: query.data?.stats,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
    createItem: createMutation.mutateAsync,
    updateItem: updateMutation.mutateAsync,
    deleteItem: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
